import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle, ArrowRight, Diamond, Award, Heart, Shield } from 'lucide-react';

export const ObruchalnyeKolcaRiga = () => {
  return (
    <>
      <Helmet>
        <title>Обручальные кольца в Риге | Свадебные кольца | Pure Atelier</title>
        <meta name="description" content="Обручальные кольца в Риге с бриллиантами. Свадебные кольца, парные кольца. Индивидуальный дизайн, лучшие цены. Pure Atelier Riga." />
        <meta name="keywords" content="обручальные кольца Рига, свадебные кольца, парные кольца с бриллиантами, wedding rings Riga" />
        <link rel="canonical" href="https://pureatelier.lv/obruchalnye-kolca-riga" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/1444442/pexels-photo-1444442.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                Pure Atelier Riga
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 mb-6 leading-tight">
                Обручальные кольца в Риге
              </h1>
              <p className="text-xl text-stone-600 mb-8 max-w-2xl mx-auto">
                Символ вечной любви. Обручальные и свадебные кольца с бриллиантами, созданные для самого важного дня.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Смотреть коллекцию <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/37128448991?text=Здравствуйте!%20Интересуют%20обручальные%20кольца." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 px-8">
                    <MessageCircle className="mr-2 h-5 w-5" /> Консультация WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Diamond, title: "С бриллиантами", desc: "Сертифицированные GIA/IGI" },
                { icon: Award, title: "Ручная работа", desc: "Изготовлено в Латвии" },
                { icon: Heart, title: "Парные кольца", desc: "Для него и для неё" },
                { icon: Shield, title: "Пожизненная гарантия", desc: "На все изделия" }
              ].map((item, i) => (
                <div key={i} className="text-center p-6">
                  <item.icon className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">
                Обручальные кольца от Pure Atelier
              </h2>
              
              <div className="prose prose-lg max-w-none text-stone-700">
                <p>
                  <strong>Pure Atelier Riga</strong> создаёт обручальные кольца, которые станут символом вашей любви на всю жизнь. 
                  Мы предлагаем как классические гладкие кольца, так и изысканные модели с бриллиантами.
                </p>
                
                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Виды обручальных колец</h3>
                <ul>
                  <li><strong>Классические обручальные</strong> — элегантные гладкие кольца из золота или платины</li>
                  <li><strong>С бриллиантовой дорожкой</strong> — кольца с рядом мелких бриллиантов</li>
                  <li><strong>Eternity кольца</strong> — бриллианты по всей окружности</li>
                  <li><strong>Парные комплекты</strong> — гармоничные кольца для пары</li>
                </ul>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Почему выбирают нас?</h3>
                <ul>
                  <li><strong>Индивидуальный подход</strong> — создадим кольца по вашему дизайну</li>
                  <li><strong>Гравировка</strong> — бесплатная гравировка имён или даты</li>
                  <li><strong>Выбор металла</strong> — белое, жёлтое, розовое золото или платина</li>
                  <li><strong>Доступные цены</strong> — лучшие цены на обручальные кольца в Риге</li>
                </ul>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Процесс заказа</h3>
                <p>
                  Закажите бесплатную консультацию, где мы поможем выбрать идеальные обручальные кольца. 
                  Мы учтём ваши пожелания по стилю, металлу и бюджету, и создадим кольца вашей мечты.
                </p>
              </div>

              <div className="mt-12 text-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Смотреть кольца <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-stone-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-serif mb-4">Планируете свадьбу?</h2>
            <p className="text-stone-300 mb-8 max-w-2xl mx-auto">
              Свяжитесь с нами для консультации по обручальным кольцам. Поможем выбрать идеальную пару для вашего особенного дня.
            </p>
            <a href="https://wa.me/37128448991?text=Здравствуйте!%20Интересуют%20обручальные%20кольца." target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8">
                <MessageCircle className="mr-2 h-5 w-5" /> Написать в WhatsApp
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default ObruchalnyeKolcaRiga;
